import type { CityConfig } from "@/pages/city-template/types";

export const forestGroveConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Oregon Vital Records (Birth, Death, Marriage)", desc: "Request certified birth, death, and marriage certificates from Oregon Vital Records.", url: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/Pages/index.aspx" },
    { icon: "ri-government-line", label: "Washington County Clerk Vital Records", desc: "Obtain vital records through the Washington County Clerk's office.", url: "https://www.washingtoncountyor.gov/CountyClerk" },
    { icon: "ri-global-line", label: "VitalChek — Order Vital Records Online", desc: "Official online ordering partner of Oregon Vital Records for certified certificates.", url: "https://www.vitalchek.com/" },
  ],
  city: "Forest Grove",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "forest-grove",
  route: "/cities/forest-grove",
  title: "Emergency Passport Forest Grove, OR | Fast Passport Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in Forest Grove, OR — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Pacific University students and Washington County wine country travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Forest Grove, Oregon",
  heroSubtitle: "Studying abroad at Pacific University? Passport expired before your wine tour of France? Fast Passport Center helps Forest Grove travelers get their passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Forest%20Grove%20Oregon%20historic%20downtown%20with%20Pacific%20University%20campus%20buildings%2C%20charming%20small%20town%20main%20street%20with%20brick%20buildings%20and%20tree-lined%20sidewalks%2C%20Washington%20County%20wine%20country%20rolling%20hills%20in%20background%2C%20lush%20Pacific%20Northwest%20greenery%2C%20warm%20golden%20afternoon%20light%2C%20photorealistic%20landscape%20photography%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=forest-grove-hero-01&orientation=landscape",
  heroImageAlt: "Forest Grove Oregon historic downtown with Pacific University and Washington County wine country hills",
  geoPosition: "45.5198;-123.1107",
  geoPlacename: "Forest Grove, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 175 miles north — 3 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Forest Grove", zips: "97116" },
    { name: "Cornelius", zips: "97113" },
    { name: "Hillsboro", zips: "97123, 97124" },
    { name: "Beaverton", zips: "97003, 97005–97008" },
    { name: "Newberg", zips: "97132" },
    { name: "McMinnville", zips: "97128" },
    { name: "Sherwood", zips: "97140" },
    { name: "Tualatin", zips: "97062" },
    { name: "Tigard", zips: "97223, 97224" },
    { name: "Portland", zips: "97201–97299" },
    { name: "Lake Oswego", zips: "97034–97035" },
    { name: "West Linn", zips: "97068" },
    { name: "Oregon City", zips: "97045" },
    { name: "Gaston", zips: "97119" },
    { name: "Banks", zips: "97106" },
    { name: "North Plains", zips: "97133" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your Forest Grove home or Pacific University dorm in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Forest Grove", desc: "FedEx and UPS locations throughout Washington County offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays — critical for study abroad and wine country travel deadlines." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Emily T.",
      location: "Forest Grove, OR",
      rating: 5,
      text: "Study abroad semester in Spain starting in 10 days and my passport expired. Fast Passport Center renewed it in 4 days. I shipped from the Hillsboro FedEx. Pacific University student approved!",
      service: "Passport Renewal",
    },
    {
      name: "James R.",
      location: "Cornelius, OR",
      rating: 5,
      text: "Wine tour of Bordeaux in two weeks and my wife's passport was damaged. Fast Passport Center handled the replacement and we had it in 6 days. Wine country travelers need this service.",
      service: "Lost Passport Replacement",
    },
    {
      name: "Michelle K.",
      location: "Forest Grove, OR",
      rating: 5,
      text: "Conference in Dublin announced with a week's notice. Fast Passport Center handled the emergency processing and I had my passport in 3 days. The daily updates were incredibly reassuring.",
      service: "Emergency Passport",
    },
  ],
  facilities: [
    {
      id: "forest-grove-post",
      name: "Forest Grove Post Office",
      type: "USPS Passport Acceptance",
      address: "2003 19th Ave",
      city: "Forest Grove",
      state: "OR",
      zip: "97116",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Downtown Forest Grove",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Forest Grove. Appointment required — book online in advance. Photo services may be available.",
      icon: "ri-mail-line",
    },
    {
      id: "hillsboro-post",
      name: "Hillsboro Main Post Office",
      type: "USPS Passport Acceptance",
      address: "1185 NE Grant St",
      city: "Hillsboro",
      state: "OR",
      zip: "97124",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm, Sat 9am–12pm",
      distance: "Hillsboro — about 8 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Hillsboro option for east Forest Grove and Pacific University students.",
      icon: "ri-mail-line",
    },
    {
      id: "cornelius-post",
      name: "Cornelius Post Office",
      type: "USPS Passport Acceptance",
      address: "100 N 9th Ave",
      city: "Cornelius",
      state: "OR",
      zip: "97113",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Cornelius — about 3 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Cornelius option for north Forest Grove residents.",
      icon: "ri-mail-line",
    },
    {
      id: "beaverton-post",
      name: "Beaverton Post Office",
      type: "USPS Passport Acceptance",
      address: "4200 SW Watson Ave",
      city: "Beaverton",
      state: "OR",
      zip: "97005",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm, Sat 9am–12pm",
      distance: "Beaverton — about 15 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Beaverton option for east Forest Grove metro residents.",
      icon: "ri-mail-line",
    },
    {
      id: "newberg-post",
      name: "Newberg Post Office",
      type: "USPS Passport Acceptance",
      address: "401 E 1st St",
      city: "Newberg",
      state: "OR",
      zip: "97132",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Newberg — about 12 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Newberg / Yamhill County option for south Forest Grove residents.",
      icon: "ri-mail-line",
    },
    {
      id: "mcminnville-post",
      name: "McMinnville Post Office",
      type: "USPS Passport Acceptance",
      address: "700 NE Baker St",
      city: "McMinnville",
      state: "OR",
      zip: "97128",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "McMinnville — about 18 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "McMinnville / wine country option for south Forest Grove residents.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Forest Grove before international travel?",
      a: "Yes. Through Fast Passport Center, Forest Grove travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today — ideal for Pacific University students and Washington County wine country travel plans.",
    },
    {
      q: "How fast can I renew my passport in Forest Grove?",
      a: "Fast passport renewal in Forest Grove is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years. No office visit required for most renewals.",
    },
    {
      q: "What if my passport expires before my study abroad semester?",
      a: "If your passport expires before a study abroad semester at Pacific University or any international program, don't wait. Most countries require your passport to be valid for at least 6 months beyond your program end date. Fast Passport Center can expedite your renewal so you don't miss your semester abroad. Start as soon as you receive your acceptance letter.",
    },
    {
      q: "Can I get a same-day passport appointment near Forest Grove?",
      a: "Same-day passport appointments at the regional passport agency in Seattle are only available for documented life-or-death emergencies and are not guaranteed. For most urgent passport situations near Forest Grove, Fast Passport Center is the faster and more predictable option — no Seattle drive required.",
    },
    {
      q: "Can I expedite a child passport in Forest Grove?",
      a: "Yes. Child passport expediting in Forest Grove is possible through Fast Passport Center, but child passports (for minors 15 and under) do require an in-person acceptance step with parental consent. We provide a clear checklist and instructions. Once the acceptance step is complete, we handle the expedited processing.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If your passport is lost or stolen before an upcoming trip, act immediately. You will need to complete Form DS-64 to report it lost or stolen and apply for a replacement. Fast Passport Center guides you through this process and can expedite your replacement passport. Don't delay — lost passport replacements take extra steps.",
    },
    {
      q: "Does Forest Grove have a passport agency?",
      a: "Forest Grove, Oregon does not have a regional passport agency. The closest regional agencies are the Seattle Passport Agency in Washington (about 175 miles north) and the San Francisco Passport Agency in California (about 640 miles south). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster and more convenient solution for most Forest Grove residents.",
    },
    {
      q: "How long does a standard passport take in Forest Grove?",
      a: "If you apply through a standard post office acceptance facility in Forest Grove, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner than that, you need expedited processing. Fast Passport Center offers Forest Grove travelers processing as fast as 24 hours.",
    },
    {
      q: "Can I get a passport renewal near me in Forest Grove without visiting an office?",
      a: "Yes. Most Forest Grove-area adults who are renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home, and ship them overnight from any FedEx or UPS location in Washington County.",
    },
    {
      q: "What airports do Forest Grove travelers typically fly from?",
      a: "Forest Grove travelers typically use Portland International Airport (PDX) — about 25 miles east — for all domestic and international departures. PDX offers direct flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver, and more. Some travelers also use Hillsboro Airport (HIO) for general aviation. Fast Passport Center can help you meet your deadline.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Forest Grove", address: "2003 19th Ave, Forest Grove, OR 97116", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/storelocator" },
    { name: "CVS — Forest Grove", address: "1953 19th Ave, Forest Grove, OR 97116", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/store-locator" },
    { name: "FedEx Office — Hillsboro", address: "2045 NE Cornell Rd, Hillsboro, OR 97124", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM", website: "https://www.fedex.com/en-us/location.html" },
    { name: "The UPS Store — Hillsboro", address: "1255 NE Cornell Rd, Hillsboro, OR 97124", hours: "Mon–Fri 8 AM – 7 PM", website: "https://www.theupsstore.com/tools/find-a-store" },
    { name: "Walmart — Hillsboro", address: "735 SE Oak St, Hillsboro, OR 97123", hours: "Daily 7 AM – 11 PM", website: "https://www.walmart.com/store/finder" },
    { name: "Walgreens — Hillsboro", address: "1255 NE Cornell Rd, Hillsboro, OR 97124", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/storelocator" },
    { name: "CVS — Hillsboro", address: "2045 NE Cornell Rd, Hillsboro, OR 97124", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/store-locator" },
    { name: "Walgreens — Cornelius", address: "100 N 9th Ave, Cornelius, OR 97113", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/storelocator" },
    { name: "CVS — Newberg", address: "401 E 1st St, Newberg, OR 97132", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/store-locator" },
    { name: "Walmart — McMinnville", address: "300 NE Highway 99W, McMinnville, OR 97128", hours: "Daily 7 AM – 11 PM", website: "https://www.walmart.com/store/finder" },
  ],
  dmvLocations: [
    {
      name: "Hillsboro DMV Office",
      address: "735 SE Oak St, Hillsboro, OR 97123",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Washington County DMV office near Forest Grove. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Beaverton DMV Office",
      address: "4200 SW Watson Ave, Beaverton, OR 97005",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Beaverton option for east Forest Grove metro residents.",
      featured: false,
    },
    {
      name: "McMinnville DMV Office",
      address: "300 NE Highway 99W, McMinnville, OR 97128",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "McMinnville option for south Forest Grove and Yamhill County residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international airport — about 25 miles east",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX offers direct international flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver, and more. The busiest travel seasons are May–August and December–January.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "Alternative hub — about 175 miles north",
      address: "17801 International Blvd, SeaTac, WA 98158",
      note: "Some Forest Grove travelers use SEA for additional flight options. Passport required for all international departures.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA Airport Website",
      featured: false,
    },
    {
      code: "HIO",
      name: "Hillsboro Airport",
      distance: "General aviation — about 8 miles east",
      address: "3355 NE Cornell Rd, Hillsboro, OR 97124",
      note: "Hillsboro Airport is general aviation only — no commercial flights. International travelers use PDX.",
      flightUrl: "https://www.portofportland.com/hio",
      flightLabel: "HIO Airport Info",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from PDX in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Forest Grove and Washington County wine country.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Forest Grove and Pacific University students renew fast — perfect for study abroad and wine country travel.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Washington County", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Forest Grove travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Forest Grove families — minors under 16 require in-person acceptance and parental consent. We provide clear instructions and checklists.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Forest Grove residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines. Same-day passport help for Forest Grove travelers.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all handled with the same expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Forest Grove, Oregon travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Forest Grove residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Forest Grove, OR residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Forest Grove travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Forest Grove families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Forest Grove, Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Forest Grove students and wine country travelers often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your trip or receive your study abroad acceptance." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and can catch these issues before your documents are submitted." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission, critical for last-minute passport renewal in Forest Grove." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. Fast Passport Center tells you exactly what you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Forest Grove runs 6–13 weeks. If your trip to France, Spain, or Italy is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly — especially for international flights out of PDX. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Forest Grove travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Forest Grove", description: "Drop off at any FedEx or UPS in the Forest Grove or Hillsboro area — most locations are open evenings. No office visit to our facility required." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (3 hours each way)", fpc: "Ship documents overnight from Forest Grove" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Portland", route: "/cities/portland" },
    { name: "Fast passport renewal in Hillsboro", route: "/cities/hillsboro" },
    { name: "Emergency passport help near Beaverton", route: "/cities/beaverton" },
    { name: "Rush passport service for Pacific University students", route: "/states/oregon" },
    { name: "Fast passport renewal for Washington County wine country travelers", route: "/states/oregon" },
  ],
  fpcOfficeAddress: "5608 17th Avenue NW, Seattle, WA 98107",
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Forest Grove or Hillsboro — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Forest Grove and all of Washington County wine country remotely",
  serviceAreaIntro: "Whether you are in downtown Forest Grove, Cornelius, Hillsboro, or anywhere in Washington County and the Portland metro, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Forest Grove-area zip codes. Anywhere in Washington, Yamhill, or Multnomah County — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Forest Grove as simple as possible. Most Forest Grove clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Forest Grove travelers. Fast Passport Center reviews your documents before submission to catch these issues before they become problems.",
  testimonialsIntro: "Real reviews from Forest Grove, Cornelius, and Washington County wine country travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Forest Grove and Pacific University travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
};